import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Check, Lock, Eye, Wallet, 
  Sprout, Heart, Zap, Shield, Target, BookOpen, Calendar,
  PenLine, ArrowRight, Play, ExternalLink, Quote, Lightbulb,
  FileText, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { courseData, getJournalPrompt } from '../data/courseData';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';
import CourseCalendar from '../components/CourseCalendar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Eye, Wallet, Sprout, Heart, Zap, Shield, Target
};

const resourceTypeIcons = {
  Book: BookOpen,
  Podcast: Play,
  Article: FileText,
  Website: ExternalLink,
  Guide: FileText,
  Community: Users
};

const CoursePage = () => {
  const [selectedDay, setSelectedDay] = useState(1);
  const [journalEntries, setJournalEntries] = useState({});
  const [currentJournal, setCurrentJournal] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [completedDays, setCompletedDays] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [activeTab, setActiveTab] = useState('content'); // content, resources, journal
  
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // Check course access
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/access/${user.email}`);
        setHasAccess(response.data.has_access);
      } catch (err) {
        console.error('Error checking access:', err);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAccess();
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    
    const saved = localStorage.getItem(`journal_${user.id}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      setJournalEntries(parsed.entries || {});
      setCompletedDays(parsed.completedDays || []);
    }
  }, [user]);

  useEffect(() => {
    if (journalEntries[selectedDay]) {
      setCurrentJournal(journalEntries[selectedDay].content);
    } else {
      setCurrentJournal('');
    }
  }, [selectedDay, journalEntries]);

  const saveJournal = () => {
    if (!currentJournal.trim() || !user) return;
    
    setIsSaving(true);
    
    const newEntries = {
      ...journalEntries,
      [selectedDay]: {
        content: currentJournal,
        date: new Date().toISOString(),
        day: selectedDay
      }
    };
    
    const newCompleted = completedDays.includes(selectedDay) 
      ? completedDays 
      : [...completedDays, selectedDay];
    
    setJournalEntries(newEntries);
    setCompletedDays(newCompleted);
    
    localStorage.setItem(`journal_${user.id}`, JSON.stringify({
      entries: newEntries,
      completedDays: newCompleted
    }));
    
    setTimeout(() => {
      setIsSaving(false);
      setSavedMessage('Journal saved!');
      setTimeout(() => setSavedMessage(''), 2000);
    }, 500);
  };

  const dayData = courseData.days[selectedDay - 1];
  const Icon = iconMap[dayData?.icon] || BookOpen;
  const progress = (completedDays.length / 7) * 100;

  if (!user) return null;

  if (checkingAccess) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
        <Header />
        <div className="pt-28 pb-20 px-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className={isDark ? 'text-stone-400' : 'text-stone-600'}>Checking access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
        <Header />
        <div className="pt-28 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className={`rounded-3xl border p-12 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                <Lock className={`w-10 h-10 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
              </div>
              <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                Course Access Required
              </h1>
              <p className={`mb-8 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                Unlock the 7 Days to Sovereignty course for just $10. Start your journey to financial, physical, and mental freedom today.
              </p>
              <Button
                onClick={() => navigate('/course/checkout')}
                className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold px-8 py-6 text-lg"
              >
                <Lock className="w-5 h-5 mr-2" />
                Get Access for $10
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showCalendar) {
    return (
      <CourseCalendar 
        journalEntries={journalEntries}
        completedDays={completedDays}
        onBack={() => setShowCalendar(false)}
        onSelectDay={(day) => {
          setSelectedDay(day);
          setShowCalendar(false);
        }}
      />
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Course Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className={`text-3xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  {courseData.title}
                </h1>
                <p className={`mt-1 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                  {courseData.subtitle}
                </p>
              </div>
              <Button
                onClick={() => setShowCalendar(true)}
                variant="outline"
                className={isDark ? 'border-stone-700 text-stone-300 hover:bg-stone-800' : 'border-stone-300 text-stone-600 hover:bg-stone-100'}
              >
                <Calendar className="w-4 h-4 mr-2" />
                View Calendar
              </Button>
            </div>
            
            {/* Progress bar */}
            <div className="flex items-center gap-4">
              <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-stone-800' : 'bg-stone-200'}`}>
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 to-green-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className={`text-sm font-medium ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                {completedDays.length}/7 days
              </span>
            </div>
          </div>

          {/* Day Navigation */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {courseData.days.map((day) => {
              const DayIcon = iconMap[day.icon] || BookOpen;
              const isCompleted = completedDays.includes(day.day);
              const isSelected = selectedDay === day.day;
              
              return (
                <button
                  key={day.day}
                  onClick={() => setSelectedDay(day.day)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                    isSelected
                      ? 'bg-amber-500 border-amber-500 text-stone-950'
                      : isCompleted
                      ? isDark
                        ? 'bg-stone-800 border-green-500/30 text-stone-300'
                        : 'bg-green-50 border-green-500/30 text-stone-700'
                      : isDark
                      ? 'bg-stone-900 border-stone-800 text-stone-400 hover:border-stone-700'
                      : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                  }`}
                >
                  {isCompleted && !isSelected ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <DayIcon className="w-4 h-4" />
                  )}
                  <span className="font-medium">Day {day.day}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'content', label: 'Lesson Content', icon: BookOpen },
              { id: 'resources', label: 'Resources', icon: ExternalLink },
              { id: 'journal', label: 'Journal', icon: PenLine }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-stone-950'
                    : isDark
                    ? 'text-stone-400 hover:text-stone-200 hover:bg-stone-800'
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              {/* Day Header Card */}
              <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                    <Icon className={`w-7 h-7 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <span className="text-amber-500 text-sm font-medium uppercase tracking-wider">
                      Day {dayData.day} • {dayData.theme}
                    </span>
                    <h2 className={`text-2xl font-bold mt-1 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                      {dayData.title}
                    </h2>
                  </div>
                </div>

                <p className={`text-lg leading-relaxed ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                  {dayData.content.intro}
                </p>
              </div>

              {/* Podcast Episode */}
              {dayData.podcastEpisode && (
                <div className={`rounded-3xl border p-6 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-red-500/10' : 'bg-red-100'}`}>
                      <Play className={`w-5 h-5 ${isDark ? 'text-red-500' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                        Recommended Episode
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                        Unshackled Living Podcast
                      </p>
                    </div>
                  </div>
                  <a
                    href={dayData.podcastEpisode.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-4 rounded-xl border transition-all group ${
                      isDark 
                        ? 'bg-stone-800/50 border-stone-700 hover:border-amber-500/30' 
                        : 'bg-stone-50 border-stone-200 hover:border-amber-500/50'
                    }`}
                  >
                    <h4 className={`font-semibold mb-1 group-hover:text-amber-500 transition-colors ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                      {dayData.podcastEpisode.title}
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                      {dayData.podcastEpisode.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-2 text-amber-500 text-sm font-medium">
                      Watch on YouTube <ExternalLink className="w-3 h-3" />
                    </span>
                  </a>
                </div>
              )}

              {/* Deep Dive */}
              {dayData.content.deepDive && (
                <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/10' : 'bg-purple-100'}`}>
                      <Lightbulb className={`w-5 h-5 ${isDark ? 'text-purple-500' : 'text-purple-600'}`} />
                    </div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                      {dayData.content.deepDive.title}
                    </h3>
                  </div>
                  <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                    {dayData.content.deepDive.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className={`leading-relaxed mb-4 ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Points */}
              <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
                <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  Key Points
                </h3>
                <div className="space-y-3">
                  {dayData.content.keyPoints.map((point, index) => (
                    <div 
                      key={index}
                      className={`flex items-start gap-3 p-4 rounded-xl ${isDark ? 'bg-stone-800/50' : 'bg-stone-50'}`}
                    >
                      <ArrowRight className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                      <span className={isDark ? 'text-stone-300' : 'text-stone-600'}>{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tutorial */}
              {dayData.content.tutorial && (
                <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
                      <FileText className={`w-5 h-5 ${isDark ? 'text-blue-500' : 'text-blue-600'}`} />
                    </div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                      {dayData.content.tutorial.title}
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {dayData.content.tutorial.steps.map((step) => (
                      <div 
                        key={step.step}
                        className={`p-5 rounded-xl border-l-4 border-amber-500 ${isDark ? 'bg-stone-800/30' : 'bg-amber-50/50'}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-amber-500/20 text-amber-500' : 'bg-amber-500 text-white'}`}>
                            {step.step}
                          </span>
                          <h4 className={`font-semibold ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                            {step.title}
                          </h4>
                        </div>
                        <p className={`ml-10 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                          {step.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Case Study */}
              {dayData.content.caseStudy && (
                <div className={`rounded-3xl border p-8 ${isDark ? 'bg-gradient-to-br from-stone-900 to-stone-900/50 border-stone-800' : 'bg-gradient-to-br from-stone-50 to-white border-stone-200 shadow-lg'}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-green-500/10' : 'bg-green-100'}`}>
                      <Users className={`w-5 h-5 ${isDark ? 'text-green-500' : 'text-green-600'}`} />
                    </div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                      Case Study: {dayData.content.caseStudy.title}
                    </h3>
                  </div>
                  <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                    {dayData.content.caseStudy.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className={`leading-relaxed mb-4 ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Expert Quote */}
              {dayData.content.expertQuote && (
                <div className={`rounded-3xl border p-8 ${isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-start gap-4">
                    <Quote className={`w-10 h-10 flex-shrink-0 ${isDark ? 'text-amber-500/50' : 'text-amber-400'}`} />
                    <div>
                      <p className={`text-xl italic leading-relaxed mb-4 ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                        "{dayData.content.expertQuote.quote}"
                      </p>
                      <p className={`font-semibold ${isDark ? 'text-amber-500' : 'text-amber-700'}`}>
                        — {dayData.content.expertQuote.author}
                      </p>
                      <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                        {dayData.content.expertQuote.context}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Today's Action */}
              <div className={`rounded-3xl border p-8 border-l-4 border-l-amber-500 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
                <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  Today's Action
                </h3>
                <p className={`text-lg ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                  {dayData.content.action}
                </p>
              </div>

              {/* Day Navigation */}
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedDay(prev => Math.max(1, prev - 1))}
                  disabled={selectedDay === 1}
                  className={isDark ? 'border-stone-700 text-stone-400 hover:bg-stone-800 disabled:opacity-30' : 'border-stone-300 text-stone-600 hover:bg-stone-100 disabled:opacity-30'}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous Day
                </Button>
                <Button
                  onClick={() => setSelectedDay(prev => Math.min(7, prev + 1))}
                  disabled={selectedDay === 7}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold disabled:opacity-50"
                >
                  Next Day
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Resources Tab */}
          {activeTab === 'resources' && (
            <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                Additional Resources for Day {selectedDay}
              </h3>
              
              {dayData.additionalResources ? (
                <div className="grid gap-4">
                  {dayData.additionalResources.map((resource, index) => {
                    const ResourceIcon = resourceTypeIcons[resource.type] || ExternalLink;
                    return (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-start gap-4 p-5 rounded-xl border transition-all group ${
                          isDark 
                            ? 'bg-stone-800/50 border-stone-700 hover:border-amber-500/30' 
                            : 'bg-stone-50 border-stone-200 hover:border-amber-500/50 hover:shadow-md'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                          <ResourceIcon className={`w-6 h-6 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={`font-semibold group-hover:text-amber-500 transition-colors ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                              {resource.title}
                            </h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-stone-700 text-stone-400' : 'bg-stone-200 text-stone-500'}`}>
                              {resource.type}
                            </span>
                          </div>
                          <p className={`text-sm mb-1 ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                            by {resource.author}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                            {resource.description}
                          </p>
                        </div>
                        <ExternalLink className={`w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                      </a>
                    );
                  })}
                </div>
              ) : (
                <p className={isDark ? 'text-stone-500' : 'text-stone-500'}>
                  No additional resources for this day yet.
                </p>
              )}
            </div>
          )}

          {/* Journal Tab */}
          {activeTab === 'journal' && (
            <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-stone-800' : 'bg-stone-100'}`}>
                  <PenLine className={`w-5 h-5 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                    Daily Journal
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                    Day {selectedDay} Reflection
                  </p>
                </div>
                {journalEntries[selectedDay] && (
                  <span className="ml-auto flex items-center gap-1 text-green-500 text-sm">
                    <Check className="w-4 h-4" />
                    Completed
                  </span>
                )}
              </div>

              {/* Journal Prompt */}
              <div className={`p-4 rounded-xl mb-4 ${isDark ? 'bg-stone-800/50' : 'bg-stone-50'}`}>
                <p className={`${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                  <span className="font-medium">Reflection prompt:</span> {getJournalPrompt(selectedDay)}
                </p>
              </div>

              {/* Journal textarea */}
              <Textarea
                value={currentJournal}
                onChange={(e) => setCurrentJournal(e.target.value)}
                placeholder="Write your thoughts, insights, and reflections here..."
                className={`min-h-[300px] resize-none mb-4 ${
                  isDark 
                    ? 'bg-stone-800 border-stone-700 text-stone-100 placeholder:text-stone-500' 
                    : 'bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400'
                }`}
              />

              {/* Save button */}
              <div className="flex items-center justify-between">
                <span className={`text-sm ${savedMessage ? 'text-green-500' : isDark ? 'text-stone-600' : 'text-stone-400'}`}>
                  {savedMessage || (journalEntries[selectedDay] ? `Last saved: ${new Date(journalEntries[selectedDay].date).toLocaleDateString()}` : '')}
                </span>
                <Button
                  onClick={saveJournal}
                  disabled={!currentJournal.trim() || isSaving}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold"
                >
                  {isSaving ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-stone-950/30 border-t-stone-950 rounded-full animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Save Journal
                    </span>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
